import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Uses DOMPurify with a restricted set of allowed tags and attributes.
 *
 * @param {string} dirtyHtml - The potentially unsafe HTML string
 * @returns {string} - Sanitized HTML safe for rendering
 */
export const sanitizeHtml = (dirtyHtml) => {
  if (!dirtyHtml || typeof dirtyHtml !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(dirtyHtml, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div'],
    ALLOWED_ATTR: ['href', 'title', 'target', 'class', 'style'],
  });
};

/**
 * Sanitize HTML for print contexts with a more permissive set of tags.
 * This is used for trusted backend content (e.g., receipts).
 *
 * @param {string} dirtyHtml - The potentially unsafe HTML string
 * @returns {string} - Sanitized HTML safe for printing
 */
export const sanitizeHtmlForPrint = (dirtyHtml) => {
  if (!dirtyHtml || typeof dirtyHtml !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(dirtyHtml, {
    WHOLE_DOCUMENT: true,
    ALLOWED_TAGS: [
      'html', 'head', 'body', 'title', 'style', 'meta', 'link',
      'div', 'span', 'p', 'br', 'hr',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
      'ul', 'ol', 'li',
      'b', 'i', 'em', 'strong', 'u',
      'img', 'a',
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'target', 'class', 'style', 'src', 'alt',
      'width', 'height', 'colspan', 'rowspan',
      'charset', 'name', 'content', 'lang', 'rel',
    ],
    ADD_TAGS: ['style'],
    FORCE_BODY: false,
  });
};

export default sanitizeHtml;
